import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { prochaskaQuestions } from '../data/prochaskaQuestions';
import { ArrowLeft, ArrowRight, Loader2, CheckCircle2, Compass } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ProchaskaTest = () => {
  const { language, t } = useLanguage();
  const navigate = useNavigate();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [selectedOption, setSelectedOption] = useState(null);
  const [loading, setLoading] = useState(false);

  const question = prochaskaQuestions[currentQuestion];
  const progress = ((currentQuestion + 1) / prochaskaQuestions.length) * 100;
  const isLastQuestion = currentQuestion === prochaskaQuestions.length - 1;

  const handleOptionSelect = (optionIndex, points, stage) => {
    setSelectedOption(optionIndex);
    setAnswers(prev => ({
      ...prev,
      [question.id]: { optionIndex, points, stage }
    }));
  };

  const handleNext = () => {
    if (selectedOption === null) {
      toast.error(language === 'fr' ? 'Veuillez sélectionner une réponse' : 'Please select an answer');
      return;
    }

    if (isLastQuestion) {
      handleSubmit();
    } else {
      setCurrentQuestion(prev => prev + 1);
      const nextQuestion = prochaskaQuestions[currentQuestion + 1];
      const prevAnswer = answers[nextQuestion.id];
      setSelectedOption(prevAnswer?.optionIndex ?? null);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
      const prevQuestion = prochaskaQuestions[currentQuestion - 1];
      const prevAnswer = answers[prevQuestion.id];
      setSelectedOption(prevAnswer?.optionIndex ?? null);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    
    try {
      const formattedAnswers = Object.entries(answers).map(([questionId, answer]) => ({
        question_id: parseInt(questionId),
        points: answer.points,
        stage: answer.stage
      }));

      const response = await axios.post(`${API_URL}/prochaska`, {
        answers: formattedAnswers
      });

      navigate('/prochaska-results', { state: { result: response.data } });
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur lors de la soumission' : 'Error submitting test');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-[#F9FAFB] py-8 sm:py-12 px-4" data-testid="prochaska-test">
      <div className="max-w-2xl mx-auto">
        {/* Progress Bar */}
        <div className="mb-8 animate-fade-in-up">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">
              {t('question')} {currentQuestion + 1} {t('of')} {prochaskaQuestions.length}
            </span>
            <span className="text-sm font-medium text-teal-600">
              {Math.round(progress)}%
            </span>
          </div>
          <Progress value={progress} className="h-2 bg-gray-200" />
        </div>

        {/* Question Card */}
        <div className="glass-card rounded-3xl p-8 sm:p-10 shadow-xl animate-fade-in-up" data-testid="question-card">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-100 rounded-full mb-6">
            <Compass className="w-4 h-4 text-teal-600" />
            <span className="text-sm font-semibold text-teal-600">
              {language === 'fr' ? 'Stades de changement' : 'Stages of change'}
            </span>
          </div>

          {/* Question Text */}
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-8 leading-relaxed">
            {language === 'fr' ? question.text_fr : question.text_en}
          </h2>

          {/* Options */}
          <div className="space-y-4 mb-8">
            {question.options.map((option, index) => (
              <button
                key={index}
                onClick={() => handleOptionSelect(index, option.points, option.stage)}
                className={`w-full option-card text-left flex items-center gap-4 ${
                  selectedOption === index ? 'selected border-teal-500 bg-teal-50' : ''
                }`}
                data-testid={`option-${index}`}
              >
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                  selectedOption === index 
                    ? 'border-teal-600 bg-teal-600' 
                    : 'border-gray-300'
                }`}>
                  {selectedOption === index && (
                    <CheckCircle2 className="w-4 h-4 text-white" />
                  )}
                </div>
                <span className={`text-lg ${selectedOption === index ? 'text-teal-700 font-medium' : 'text-gray-700'}`}>
                  {language === 'fr' ? option.label_fr : option.label_en}
                </span>
              </button>
            ))}
          </div>

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between pt-6 border-t border-gray-100">
            <Button
              variant="ghost"
              onClick={handlePrevious}
              disabled={currentQuestion === 0}
              className="rounded-full"
              data-testid="prev-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('previous')}
            </Button>

            <Button
              onClick={handleNext}
              disabled={loading || selectedOption === null}
              className="rounded-full bg-teal-600 hover:bg-teal-700 shadow-lg px-8"
              data-testid="next-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : isLastQuestion ? (
                <>
                  {t('submit')}
                  <CheckCircle2 className="ml-2 w-4 h-4" />
                </>
              ) : (
                <>
                  {t('next')}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Progress Dots */}
        <div className="flex items-center justify-center gap-2 mt-8">
          {prochaskaQuestions.map((_, index) => (
            <div
              key={index}
              className={`w-2 h-2 rounded-full transition-all ${
                index === currentQuestion 
                  ? 'w-6 bg-teal-600' 
                  : index < currentQuestion || answers[prochaskaQuestions[index].id]
                    ? 'bg-teal-400'
                    : 'bg-gray-300'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
